import {observable, action, makeObservable} from 'mobx'
import {Location, NavigateFunction} from 'react-router'

export interface Watcher {
    isDirty: boolean
}

export interface CustomAction {
    /**
     * ValidationState dialog button callback. Depending on returned value the dialog will do different things:
     * - true -> close dialog and route
     * - false -> close dialog
     * - null -> nothing (keep dialog open)
     * - void (undefined) -> close dialog and route
     */
    callback?: () => Promise<boolean | null | void> | void | boolean | null
    label: string
}

function isTestEnvironment(): boolean {
    return import.meta.env['NODE_ENV'] === 'test'
}

const defaultErrorMessage = 'You have unsaved data.'

export class ValidationState {
    private watcherStack: Array<Watcher>
    private route: string | undefined
    // TODO rename to "isDirty" when new OC views are finished
    public _isDirty = false

    public navigate?: NavigateFunction
    public location?: Location<any>

    @observable public errorMessage: string
    @observable public actions: Array<CustomAction> | undefined

    constructor() {
        makeObservable(this)
        this.watcherStack = []
        this.errorMessage = ''
    }

    // TODO replace "doActionAndRoute" with following when new OC views are finished:
    /*
    @action.bound
    public async doActionAndRoute(action: () => Promise<void>): Promise<void> {
        try {
            this.clearErrorMessage()
            await action()

            if (this.route) {
                history.push(this.route)
            }
        } catch (exc) {
            snackHandler.error(`Action failed: ${exc.message}`)
        }
    }
    */

    @action.bound
    public async doActionAndRoute(
        action: (() => Promise<boolean | null | void> | void | boolean | null) | undefined
    ): Promise<void> {
        this.clearErrorMessage()
        if (action) {
            let ret = action()

            if (ret instanceof Promise) {
                ret = await ret
            }

            if (ret === null || ret === false) {
                return
            }
        }

        if (this.route) {
            this.navigate?.(this.route)
            this.route = undefined
        }
    }

    @action.bound
    public clearRoute() {
        this.route = undefined
    }

    @action.bound
    public addComponentStateToWatcherStack(componentState: Watcher): void {
        this.watcherStack.push(componentState)
    }

    @action.bound
    public removeComponentStateFromWatcherStack(componentState: Watcher): void {
        this.watcherStack.splice(this.watcherStack.indexOf(componentState), 1)
    }

    @action.bound
    public setActions(actions: CustomAction[]): void {
        this.actions = actions.map(a => ({
            callback: () => this.doActionAndRoute(a.callback),
            label: a.label
        }))
    }

    @action.bound
    public clearActions(): void {
        this.actions = undefined
    }

    public isDirty(): boolean {
        return this._isDirty || this.isAnyDirty(this.watcherStack)
    }

    @action.bound
    public setErrorMessage(message = defaultErrorMessage): void {
        this.errorMessage = message
    }

    public beforeUnload(e: BeforeUnloadEvent): boolean {
        e.preventDefault()
        e.returnValue = defaultErrorMessage
        return true
    }

    @action.bound
    public clearErrorMessage(): void {
        this.errorMessage = ''
    }

    @action.bound
    public pushToHistory(route: string): void {
        if (isTestEnvironment()) {
            return
        }

        if (route === this.location?.pathname) {
            return
        }

        if (this.isDirty()) {
            this.route = route
            this.setErrorMessage()
        } else {
            this.navigate?.(route)
        }
    }

    private isAnyDirty(arr: Array<any>): boolean {
        return !!arr.find(s => s.isDirty)
    }
}
